import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivityRole } from '../../../../api/activity/lib/activity.interfaces';
import { ContractType } from '../../../../api/contract/lib/contract.interfaces';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  CreateActivityData,
  PersonalizedRole,
} from './create-activity.interfaces';
import { AutocompleteStaffComponent } from '../../../../util/components/autocomplete/wrappers/autocomplete-staff.component';
import { ApplyPipe } from '../../../../util/apply.pipe';
import { ValueChangesPipe } from '../../../../util/forms/value-changes.pipe';
import { map, Observable, of, startWith, switchMap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { EligibilityService } from '../../../../api/contract/eligibility.service';
import { ContractTypeEligibility } from '../../../../api/contract/lib/eligibility.interfaces';
import { Staff } from '../../../../api/staff/staff.model';
import { MatIcon } from '@angular/material/icon';
import {
  getScheduledHours,
  getTargetSchedule,
  getTargetScheduleInterval,
} from '../../../../api/_lib/schedule';
import { MatTooltip } from '@angular/material/tooltip';
import { watchFormKeys } from '../../../../util/forms/watch-form-keys';
import { toSignal } from '@angular/core/rxjs-interop';
import { activityRoleOptions } from '../../../../api/activity/lib/activity.util';
import { contractTypeOptions } from '../../../../api/contract/lib/contract.util';
import { duplicateActivityValidator } from './duplicate-activity-validator';
import { ActivityService } from '../../../../api/activity/activity.service';
import { wrapHasTarget } from '../../../../util/target.util';
import { MainActivityPickerComponent } from './main-activity/main-activity-picker.component';
import { Activity } from '../../../../api/activity/activity.model';
import { AdmonitionComponent } from '../../../../util/components/admonition/admonition.component';
import { i18nICUAttribute } from '../../../../util/i18n/i18n-attributes';
import { HourlyPayFormFieldComponent } from '../../../../util/wage/hourly-pay-form-field/hourly-pay-form-field.component';

@Component({
  selector: 'app-create-activity-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatLabel,
    MatInputModule,
    MatSelectModule,
    AutocompleteStaffComponent,
    ApplyPipe,
    ValueChangesPipe,
    AsyncPipe,
    MatIcon,
    MatTooltip,
    MainActivityPickerComponent,
    AdmonitionComponent,
    HourlyPayFormFieldComponent,
  ],
  templateUrl: './create-activity-dialog.component.html',
})
export class CreateActivityDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(
    MatDialogRef<CreateActivityDialogComponent>,
  );
  private readonly activityService = inject(ActivityService);
  private readonly eligibilityService = inject(EligibilityService);
  protected readonly dialogData = inject<CreateActivityData>(MAT_DIALOG_DATA);
  protected readonly pseudoActivity = wrapHasTarget(
    this.dialogData.target,
    this.dialogData.staff,
  );
  protected readonly automaticWage = signal(true);
  protected readonly contractTypes = contractTypeOptions;
  protected readonly activityRoles = activityRoleOptions.map((item) =>
    this.personalizeRole(item),
  );

  form = this.fb.nonNullable.group({
    staff: [
      // ! to exclude undefined from FormControl type.
      { value: this.dialogData.staff!, disabled: !!this.dialogData.staff },
      [Validators.required],
      [duplicateActivityValidator(this.activityService, this.pseudoActivity)],
    ],
    type: [ContractType.Monitor, Validators.required],
    hourlyPay: [0, [Validators.required, Validators.min(0.1)]],
    hours: [
      {
        value: getScheduledHours(getTargetSchedule(this.dialogData.target)),
        disabled: true,
      },
    ],
    role: [
      this.dialogData.appliedRoles?.[0] ?? ActivityRole.Monitor,
      Validators.required,
    ],
  });
  protected readonly overlappingActivities = toSignal(
    watchFormKeys(this.form, 'staff', 'role').pipe(
      switchMap(({ staff, role }) => {
        const pseudoActivity = wrapHasTarget(
          this.dialogData.target,
          staff,
          role,
        );
        return staff
          ? this.activityService.findOverlappingActivities(pseudoActivity).pipe(
              startWith(undefined),
              map((overlapping) =>
                overlapping?.length
                  ? [...overlapping, pseudoActivity]
                  : overlapping,
              ),
            )
          : of(undefined);
      }),
    ),
  );

  protected readonly selectedMainActivity = signal<Activity | null>(null);

  onSubmit() {
    const form = this.form.getRawValue();
    this.dialogRef.close({
      activityData: {
        staff: form.staff,
        role: form.role,
        contractData: {
          type: form.type,
          hourlyPay:
            form.type === ContractType.GeenContract
              ? 0
              : this.automaticWage()
                ? null
                : form.hourlyPay,
        },
      },
      mainActivity: this.selectedMainActivity(),
      overlappingActivities: this.overlappingActivities(),
    });
  }

  getContractTypeTriggerText(value: ContractType) {
    return (
      contractTypeOptions.find((item) => item.value === value) ?? {
        title: $localize`Unknown contract type`,
      }
    ).title;
  }

  getActivityRoleTriggerText(value: ActivityRole) {
    return (
      activityRoleOptions.find((item) => item.value === value) ?? {
        title: $localize`Unknown activity role`,
      }
    ).title;
  }

  checkEligibility = (
    staff: Staff,
    contractType: ContractType,
  ): Observable<ContractTypeEligibility> =>
    staff
      ? this.eligibilityService.checkEligibility(staff, contractType)
      : // Allow all contract types when no staff is selected.
        of({ eligible: true });

  personalizeRole(item: PersonalizedRole): PersonalizedRole {
    item = { ...item };
    const remarks = [];
    if (
      this.dialogData.appliedRoles &&
      !this.dialogData.appliedRoles.includes(item.value)
    )
      remarks.push('notApplied');
    if (
      this.dialogData.openSpots &&
      !this.dialogData.openSpots.includes(item.value)
    )
      remarks.push('notOpen');
    if (remarks.length)
      item.remarks = Object.fromEntries(
        remarks.map((remark) => [remark, true]),
      );
    return item;
  }

  protected readonly i18nICUAttribute = i18nICUAttribute;
  protected readonly ContractType = ContractType;
  protected readonly getTargetScheduleInterval = getTargetScheduleInterval;
  protected readonly startWith = startWith;
  protected readonly ActivityRole = ActivityRole;
}
